/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Static release notes for banner/dialog; edit and redeploy — newest entry first. */

export type ReleaseTag = 'feature' | 'improvement' | 'fix';

export interface ReleaseNote {
  version: string;
  date: string;
  title: string;
  /** Lead-in shown under the title, before the highlights. */
  intro?: string;
  highlights: { tag: ReleaseTag; text: string }[];
  /** Closing line shown after the highlights. */
  outro?: string;
  /** Left out of the dialog and the banner; the entry and its strings stay so it can be shown again. */
  hidden?: boolean;
}

/** Every note, hidden ones included. */
export const ALL_RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '2026.23',
    date: '2026-10-07',
    title: 'releaseNotes.v2026_23.title',
    intro: 'releaseNotes.v2026_23.intro',
    highlights: [
      { tag: 'feature', text: 'releaseNotes.v2026_23.highlights.h0' },
      { tag: 'improvement', text: 'releaseNotes.v2026_23.highlights.h1' },
      { tag: 'fix', text: 'releaseNotes.v2026_23.highlights.h2' },
    ],
    outro: 'releaseNotes.v2026_23.outro',
  },
  {
    version: '2026.22',
    date: '2026-09-15',
    title: 'releaseNotes.v2026_22.title',
    highlights: [
      { tag: 'feature', text: 'releaseNotes.v2026_22.highlights.h0' },
      { tag: 'improvement', text: 'releaseNotes.v2026_22.highlights.h1' },
      { tag: 'fix', text: 'releaseNotes.v2026_22.highlights.h2' },
    ],
  },
  {
    version: '2026.21',
    date: '2026-08-12',
    title: 'releaseNotes.v2026_21.title',
    highlights: [
      { tag: 'feature', text: 'releaseNotes.v2026_21.highlights.h0' },
      { tag: 'improvement', text: 'releaseNotes.v2026_21.highlights.h1' },
      { tag: 'fix', text: 'releaseNotes.v2026_21.highlights.h2' },
    ],
    hidden: true,
  },
];

/** The notes the app shows. */
export const RELEASE_NOTES = ALL_RELEASE_NOTES.filter((note) => !note.hidden);

/** Current app release (newest note). */
/* v8 ignore next -- defensive: RELEASE_NOTES is a static, non-empty list. */
export const LATEST_VERSION = RELEASE_NOTES[0]?.version ?? '';

/** Release date of the current app release (newest note). */
/* v8 ignore next -- defensive: RELEASE_NOTES is a static, non-empty list. */
export const LATEST_RELEASE_DATE = RELEASE_NOTES[0]?.date ?? '';

/**
 * Renders a `YYYY-MM-DD` release date in long form (e.g. "August 12, 2026"). Pass the app
 * language so the date follows the language the user picked rather than the browser's.
 */
export const formatReleaseDate = (iso: string, locale?: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
